import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Area, AreaChart
} from 'recharts';
import { formatCurrency, formatPercentage } from './performanceUtils';
import { INITIAL_RC_DIM } from '../../utils/rechartsDefaults';

const formatDate = (dateString, selectedPeriod) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: selectedPeriod === '1Y' || selectedPeriod === 'ALL' ? 'numeric' : undefined
  });
};

const CustomTooltip = ({ active, payload, label, selectedPeriod }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-4 border border-slate-200 rounded-lg shadow-lg">
        <p className="text-slate-600 text-sm mb-2">{formatDate(label, selectedPeriod)}</p>
        <div className="space-y-1">
          <p className="text-slate-900 font-medium">
            Portfolio Value: <span className="font-mono">{formatCurrency(data.current_value)}</span>
          </p>
          <p className="text-slate-600">
            Invested: <span className="font-mono">{formatCurrency(data.total_invested)}</span>
          </p>
          <p className={`font-medium ${data.profit_loss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            P&L: <span className="font-mono">{formatCurrency(data.profit_loss)} ({formatPercentage(data.profit_loss_percentage)})</span>
          </p>
          <p className="text-slate-600 text-sm">Investments: {data.investment_count}</p>
        </div>
      </div>
    );
  }
  return null;
};

const AREA_CHART_MARGIN = { top: 10, right: 30, left: 12, bottom: 0 };
const PL_TOOLTIP_STYLE = { backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' };
const PL_DOT_STYLE = { fill: '#10B981', strokeWidth: 2, r: 4 };

const PerformanceCharts = ({ performanceData, selectedPeriod }) => {
  const fmtDate = (d) => formatDate(d, selectedPeriod);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
      {/* Portfolio Value Chart */}
      <Card className="portfolio-card">
        <CardHeader>
          <CardTitle className="text-xl font-serif flex items-center justify-between">
            Portfolio Value Over Time
            <Badge variant="outline" className="font-mono text-xs">
              {selectedPeriod} &bull; {performanceData.length} data points
            </Badge>
          </CardTitle>
          <CardDescription className="body-text">
            Track your total portfolio value and invested amount over {selectedPeriod.toLowerCase()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80" data-sensitive="chart">
            <ResponsiveContainer width="100%" height="100%" debounce={1} initialDimension={INITIAL_RC_DIM}>
              <AreaChart data={performanceData} margin={AREA_CHART_MARGIN}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorInvested" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6B7280" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#6B7280" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={12} tickFormatter={fmtDate} />
                <YAxis stroke="#64748B" fontSize={12} width={80} tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip content={<CustomTooltip selectedPeriod={selectedPeriod} />} />
                <Legend />
                <Area type="monotone" dataKey="total_invested" stackId="1" stroke="#6B7280" fill="url(#colorInvested)" strokeWidth={2} name="Total Invested" />
                <Area type="monotone" dataKey="current_value" stackId="2" stroke="#10B981" fill="url(#colorValue)" strokeWidth={3} name="Current Value" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Profit/Loss Chart */}
      <Card className="portfolio-card">
        <CardHeader>
          <CardTitle className="text-xl font-serif">Profit & Loss Trend</CardTitle>
          <CardDescription className="body-text">
            Track your portfolio gains and losses over {selectedPeriod.toLowerCase()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80" data-sensitive="chart">
            <ResponsiveContainer width="100%" height="100%" debounce={1} initialDimension={INITIAL_RC_DIM}>
              <LineChart data={performanceData} margin={AREA_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={12} tickFormatter={fmtDate} />
                <YAxis stroke="#64748B" fontSize={12} width={80} tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip
                  contentStyle={PL_TOOLTIP_STYLE}
                  formatter={(value, name) => [formatCurrency(value), name === 'profit_loss' ? 'Profit/Loss' : name]}
                  labelFormatter={(label) => fmtDate(label)}
                />
                <Line type="monotone" dataKey="profit_loss" stroke="#10B981" strokeWidth={3} dot={PL_DOT_STYLE} name="Profit/Loss" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PerformanceCharts;
